import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Rocket, Share2, Link2, Download, Check } from "lucide-react";
import { toast } from "sonner";
import { LAUNCH_DATE, pad, useCountdown } from "@/lib/launch";

const SHARE_URL = () => `https://usewylo.com/?ref=waitlist`;
const shareText = (days) =>
  `I'm on the WYLO list. WYLO goes live on 1 November 2026 - ${days} days to go. Get back to deep focus, without surveillance.`;

function drawCard(days, hours, minutes) {
  const c = document.createElement("canvas");
  c.width = 1200;
  c.height = 630;
  const x = c.getContext("2d");
  const g = x.createLinearGradient(0, 0, 1200, 630);
  g.addColorStop(0, "#1A3A5C");
  g.addColorStop(1, "#0F2540");
  x.fillStyle = g;
  x.fillRect(0, 0, 1200, 630);
  x.fillStyle = "rgba(44,107,172,0.35)";
  x.beginPath(); x.arc(1050, 80, 260, 0, Math.PI * 2); x.fill();
  x.fillStyle = "#F8F9FC";
  x.fillRect(80, 80, 56, 56);
  x.fillStyle = "#1A3A5C";
  x.font = "bold 30px sans-serif";
  x.fillText("W", 92, 121);
  x.fillStyle = "#FFFFFF";
  x.font = "bold 40px sans-serif";
  x.fillText("wylo", 152, 122);
  x.fillStyle = "#7DE3B4";
  x.font = "600 22px sans-serif";
  x.fillText("LAUNCH WAITLIST", 80, 220);
  x.fillStyle = "#FFFFFF";
  x.font = "bold 76px sans-serif";
  x.fillText("I'm on the WYLO list.", 80, 305);
  x.fillStyle = "rgba(232,236,242,0.85)";
  x.font = "30px sans-serif";
  x.fillText("Where You Left Off - live on 1 November 2026", 80, 360);
  const cells = [[days, "DAYS"], [pad(hours), "HOURS"], [pad(minutes), "MINUTES"]];
  cells.forEach(([v, l], i) => {
    const bx = 80 + i * 200;
    x.fillStyle = "rgba(255,255,255,0.10)";
    x.fillRect(bx, 410, 180, 130);
    x.fillStyle = "#FFFFFF";
    x.font = "bold 64px sans-serif";
    x.textAlign = "center";
    x.fillText(String(v), bx + 90, 485);
    x.fillStyle = "rgba(232,236,242,0.7)";
    x.font = "600 18px sans-serif";
    x.fillText(l, bx + 90, 522);
    x.textAlign = "left";
  });
  x.fillStyle = "rgba(232,236,242,0.7)";
  x.font = "24px sans-serif";
  x.fillText("usewylo.com  ·  Privacy-first focus for people and teams", 80, 590);
  return c.toDataURL("image/png");
}

export default function ShareLaunchCard() {
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_DATE);
  const [copied, setCopied] = useState(false);
  const text = shareText(days);
  const url = SHARE_URL();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Please copy the link manually.");
    }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = drawCard(days, hours, minutes);
    a.download = "im-on-the-wylo-list.png";
    a.click();
  };

  const native = async () => {
    try {
      await navigator.share({ title: "I'm on the WYLO list", text, url });
    } catch {}
  };

  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=600,height=520");

  return (
    <div className="mt-8 text-left" data-testid="share-launch-card">
      <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-3 text-center">Tell your team</p>

      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#1A3A5C] to-[#0F2540] text-white p-6" data-testid="share-card-preview">
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#2C6BAC]/40 blur-2xl" />
        <div className="relative flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[#F8F9FC] flex items-center justify-center">
            <span className="font-display font-bold text-[#1A3A5C] text-xs">W</span>
          </div>
          <span className="font-display font-bold tracking-tighter">wylo</span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#7DE3B4] font-semibold">
            <Rocket size={11} /> Launch waitlist
          </span>
        </div>
        <p className="relative font-display text-2xl md:text-3xl tracking-tighter mt-4">I'm on the WYLO list.</p>
        <p className="relative text-xs text-[#E8ECF2]/80 mt-1">Where You Left Off - live on 1 November 2026</p>
        <div className="relative grid grid-cols-4 gap-2 mt-4 max-w-sm" data-testid="share-card-countdown">
          {[["Days", days], ["Hours", pad(hours)], ["Minutes", pad(minutes)], ["Seconds", pad(seconds)]].map(([l, v], i) => (
            <div key={l} className="rounded-md bg-white/10 text-center py-2">
              <p key={v} className={`font-display font-bold text-xl tabular-nums ${i === 3 ? "animate-[cd-pulse_1s_ease-out]" : ""}`}>{v}</p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#E8ECF2]/70">{l}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <Button variant="outline" size="sm" className="border-[#E8ECF2]" data-testid="share-x"
          onClick={() => open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`)}>
          <XLogo /> Share on X
        </Button>
        <Button variant="outline" size="sm" className="border-[#E8ECF2]" data-testid="share-linkedin"
          onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`)}>
          <LinkedInLogo /> LinkedIn
        </Button>
        <Button variant="outline" size="sm" className="border-[#E8ECF2]" onClick={copy} data-testid="share-copy">
          {copied ? <Check size={14} className="mr-1.5 text-[#1D9E75]" /> : <Link2 size={14} className="mr-1.5" />} {copied ? "Copied" : "Copy link"}
        </Button>
        <Button variant="outline" size="sm" className="border-[#E8ECF2]" onClick={download} data-testid="share-download">
          <Download size={14} className="mr-1.5" /> Download card
        </Button>
        {typeof navigator !== "undefined" && navigator.share && (
          <Button variant="outline" size="sm" className="border-[#E8ECF2]" onClick={native} data-testid="share-native">
            <Share2 size={14} className="mr-1.5" /> More
          </Button>
        )}
      </div>
    </div>
  );
}

function XLogo() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="mr-1.5" aria-hidden>
      <path d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.9l-5.4-7.06L3.9 22H.64l8.02-9.17L.5 2h7.07l4.88 6.45L18.24 2Zm-1.21 18h1.8L7.05 3.9H5.12L17.03 20Z" />
    </svg>
  );
}

function LinkedInLogo() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="mr-1.5" aria-hidden>
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z" />
    </svg>
  );
}
